import { RootState } from "../../../../reducers";
import { useState, useEffect } from "react";
import FixtureBetButton from "../FixtureBetButton";
import Chart from "../Chart";
import {
  TeamsRow,
  TeamContainer,
  RightContainer,
  Container,
  CompetitionName,
  CompetitionNameContainer,
} from "../MatchCard/styles";
import {
  Percentage,
  StatsItem,
  StatsPanel,
  ChartPanel,
  ChartIconPanel,
  ChartIcon,
  Team,
  FixturePanel,
} from "../PlayerCard/styles";
import FormIndicator from "../FormIndicator";
import {
  Contestant,
  Form,
  PlayerStatsType,
  Selections,
  Meta,
} from "../../../../types/fixture";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import ButtonStatsOpen from "../../../../assets/button_stats_open.png";
import ButtonStatsClose from "../../../../assets/button_stats_close.png";
import { useSelector, useDispatch } from "react-redux";
import { getPlayerDetails } from "../../../../utils/api";
import { isOverUnderSelectionType, getBasedOn } from "../../../../utils";

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  id: string;
  contestant: Contestant[];
  date: string;
  selections: Selections[];
  form: Form;
  marketName: string;
  stats: PlayerStatsType;
  meta: Meta;
}

function PlayerCard({
  id,
  contestant,
  date,
  selections,
  marketName,
  stats,
  meta,
}: Props): JSX.Element {
  const dispatch = useDispatch();
  const filterStore = useSelector((state: RootState) => state.filter);

  const [chartData, setChartData] = useState(
    getBasedOn(
      filterStore.competitions[0],
      filterStore.configData.basedOn
    ).reduce((acc, { type }) => {
      return { ...acc, [type]: null };
    }, {})
  );
  const [chartOpen, setChartOpen] = useState(false);
  const [chartBasedOn, setChartBasedOn] = useState(filterStore.statsFilter);

  const [showPrevLines, setShowPrevLines] = useState(false);

  const fetchPlayersDetailData = async () => {
    dispatch({ type: "SET_CHART_LOADING", payload: true });
    const result = await getPlayerDetails(
      stats.playerId,
      stats.teamId,
      id,
      filterStore.market,
      chartBasedOn
    );
    setChartData(
      Object.assign({}, chartData, {
        [chartBasedOn]: result.data.data,
      })
    );
    dispatch({ type: "SET_CHART_LOADING", payload: false });
  };

  useEffect(() => {
    if (chartOpen && !chartData[chartBasedOn as keyof typeof chartData]) {
      fetchPlayersDetailData();
    }
  }, [chartBasedOn]);

  const isScorerMarketType = () => {
    return (
      marketName === "First Touchdown Scorer" ||
      marketName === "Anytime Touchdown Scorer" ||
      marketName === "First Basket Scorer"
    );
  };

  const renderStatsLeft = () => {
    switch (marketName) {
      case "First Touchdown Scorer":
        return "First Touchdown";
      case "Anytime Touchdown Scorer":
        return "Touchdowns";
      case "First Basket Scorer":
        return "First Basket";
      default:
        return null;
    }
  };

  const renderStatsRight = () => {
    switch (marketName) {
      case "First Touchdown Scorer":
        return "First Touchdown Scored";
      case "Anytime Touchdown Scorer":
        return "1+ Touchdown Scored";
      case "First Basket Scorer":
        return "First Basket Scored";
      default:
        return null;
    }
  };

  const selectionHandicapValue = selections[0] && selections[0].hasOwnProperty('handicap')
    ? parseFloat(selections[0].handicap)
    : 0;

  return (
    <Container>
      <CompetitionNameContainer>
        <CompetitionName>
          {meta.competition.id} •{" "}
          <span className="time">
            {dayjs.utc(date).local().format("ddd D MMM hh:mmA")}
          </span>
        </CompetitionName>
      </CompetitionNameContainer>

      <TeamsRow>
        <TeamContainer>
          <img
            src={`https://cdn.checkd-dev.com/bettinghub/teams/${stats.teamId}.png`}
            alt={stats.teamId}
            loading="lazy"
          />
          <p>{selections[0].name}</p>
        </TeamContainer>
        <RightContainer>
          {isScorerMarketType() ? (
            <FormIndicator form={stats.form} />
          ) : (
            <ChartIconPanel>
              <Percentage isGreen={Math.round(stats.probability) >= 50}>
                <b>{`${Math.round(stats.probability)}%`}</b>
              </Percentage>
              {
                isOverUnderSelectionType(selections) && <ChartIcon
                  onClick={async () => {
                    if (
                      !chartOpen &&
                      !chartData[chartBasedOn as keyof typeof chartData]
                    ) {
                      await fetchPlayersDetailData();
                    }
                    setChartOpen(!chartOpen);
                  }}
                >
                  <img
                    src={chartOpen ? ButtonStatsOpen : ButtonStatsClose}
                    alt="back-arrow"
                    loading="lazy"
                  />
                </ChartIcon>
              }
            </ChartIconPanel>
          )}
        </RightContainer>
      </TeamsRow>

      <FixturePanel>
        <Team selected={contestant[1].id === stats.teamId}>
          {contestant[1].name}
        </Team>{" "}
        @{" "}
        <Team selected={contestant[0].id === stats.teamId}>
          {contestant[0].name}
        </Team>
      </FixturePanel>

      {isScorerMarketType() ? (
        <StatsPanel>
          <StatsItem>
            <div>
              <b>
                {stats.count} / {stats.total}
              </b>
            </div>
            <div>{renderStatsLeft()} / Games</div>
          </StatsItem>
          <StatsItem>
            <Percentage isGreen={Math.round(stats.probability) >= 50}>
              <b>{`${Math.round(stats.probability)}%`}</b>
            </Percentage>
            <div>{renderStatsRight()}</div>
          </StatsItem>
        </StatsPanel>
      ) : (
        <div>
          {chartOpen && (
            <ChartPanel>
              <Chart
                showPrevLines={showPrevLines}
                chartBasedOn={chartBasedOn}
                data={chartData[chartBasedOn as keyof typeof chartData]}
                selectionHandicap={selectionHandicapValue}
                handleOnClick={(type) => {
                  setChartBasedOn(type);
                }}
                handleOnSwitch={() => {
                  setShowPrevLines(!showPrevLines);
                }}
              />
            </ChartPanel>
          )}
        </div>
      )}

      <FixtureBetButton
        selections={selections}
        market={marketName}
        competition={meta?.competition?.id}
        eventContestants={contestant}
      />
    </Container>
  );
}

export default PlayerCard;
